import type {
  Operation as OperationApiResponse,
  PreorderScheduling as PreorderSchedulingApi,
  PreparationTime,
} from '@wix/ambassador-restaurants-operations-v1-operation/types';
import {
  SchedulingType,
  FulfillmentType,
  AsapFutureHandlingType,
} from '@wix/ambassador-restaurants-operations-v1-operation/types';

import type {
  Operation,
  AsapScheduling,
  PreorderScheduling,
} from '../../types/operation';
import { DispatchType } from '../../types/businessTypes';
import { toMinutes } from '../../utils/time';

const FULFILLMENT_TO_DISPATCH: Partial<Record<FulfillmentType, DispatchType>> =
  {
    [FulfillmentType.DELIVERY]: DispatchType.DELIVERY,
    [FulfillmentType.DINE_IN]: DispatchType.DINE_IN,
  };

const getPreOrderOptions = (
  preorderOptions?: PreorderSchedulingApi,
): PreorderScheduling | undefined => {
  const timeBounded = preorderOptions?.method?.timeBoundedOptions;

  if (!timeBounded) {
    return;
  }

  const timeWindows =
    preorderOptions?.fulfillmentTimesDisplay?.timeWindowsOptions;

  return {
    timeInAdvance: {
      min: toMinutes(
        timeBounded.minTimeInAdvance?.duration ?? 0,
        timeBounded.minTimeInAdvance?.timeUnit,
      ),
      max: toMinutes(
        timeBounded.maxTimeInAdvance?.duration ?? 0,
        timeBounded.maxTimeInAdvance?.timeUnit,
      ),
    },
    timeWindowDuration: toMinutes(
      timeWindows?.duration ?? 0,
      timeWindows?.timeUnit,
    ),
  };
};

const getAsapOptions = (prep?: PreparationTime): AsapScheduling | undefined => {
  if (!prep) {
    return;
  }

  const { maxTimeOptions, timeRangeOptions } = prep;

  if (maxTimeOptions?.duration != null && maxTimeOptions.timeUnit) {
    return {
      maxInMinutes: toMinutes(maxTimeOptions.duration, maxTimeOptions.timeUnit),
    };
  }

  if (
    timeRangeOptions?.timeUnit &&
    timeRangeOptions.minDuration != null &&
    timeRangeOptions.maxDuration != null
  ) {
    return {
      rangeInMinutes: {
        min: toMinutes(timeRangeOptions.minDuration, timeRangeOptions.timeUnit),
        max: toMinutes(timeRangeOptions.maxDuration, timeRangeOptions.timeUnit),
      },
    };
  }
};

/** Convert the Operation from API into our domain Operation type. */
export const normalizeOperation = (
  operation: OperationApiResponse,
): Operation => {
  const { orderScheduling, defaultFulfillmentType } = operation;
  const asapOpts = orderScheduling?.asapOptions;
  const daysCount = asapOpts?.businessDaysAheadHandlingOptions?.daysCount ?? 0;

  const schedulingType = orderScheduling?.type ?? SchedulingType.ASAP;
  const allowAsapFutureHandling =
    asapOpts?.asapFutureHandlingType ===
      AsapFutureHandlingType.BUSINESS_DAYS_AHEAD_HANDLING &&
    Number(daysCount) >= 0;
  const defaultDispatchType =
    FULFILLMENT_TO_DISPATCH[defaultFulfillmentType!] ?? DispatchType.PICKUP;

  return {
    id: operation.id || '',
    name: operation.name || '',
    enabled: !!operation.enabled,
    fulfillmentIds: operation.fulfillmentIds || [],
    asapOptions: getAsapOptions(asapOpts?.preparationTime),
    preOrderOptions: getPreOrderOptions(orderScheduling?.preorderOptions),
    schedulingType,
    allowAsapFutureHandling,
    businessDaysAheadHandlingOptions: daysCount,
    defaultDispatchType,
    operationGroupId: operation.operationGroupId || '',
    locationId: operation.businessLocationId || '',
    locationDetails: operation.businessLocationDetails,
  };
};
